import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import type { PerformancePeriod } from '../../types/performance';

interface PerformanceSendSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: 'user' | 'team';
  userId?: string;
  period: PerformancePeriod;
  defaultTo?: string;
}

function errorMessage(err: unknown): string {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return 'Failed to send';
  }
  const data = (err as { response?: { data?: { error?: unknown } } }).response
    ?.data?.error;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message: string }).message);
  }
  return 'Failed to send';
}

export const PerformanceSendSheet: React.FC<PerformanceSendSheetProps> = ({
  open,
  onOpenChange,
  scope,
  userId,
  period,
  defaultTo = '',
}) => {
  const [to, setTo] = useState(defaultTo);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setTo(defaultTo);
  }, [open, defaultTo]);

  const send = async () => {
    const recipient = to.trim();
    if (!recipient) {
      toast.error('Enter an email address');
      return;
    }
    setSending(true);
    try {
      await api.post('/performance/email', {
        scope,
        user_id: userId,
        period,
        to: recipient,
      });
      toast.success('Overview sent');
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[1000]" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            bg-white rounded-2xl w-full max-w-md p-5 z-[1001] space-y-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-black text-gray-900">
                Send overview
              </Dialog.Title>
              <p className="text-xs text-gray-500 mt-0.5">
                PDF attached via email
              </p>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="p-2 text-gray-400" aria-label="Close">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              To
            </span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full min-h-[44px] px-3 rounded-xl border border-gray-200
                text-base text-gray-900"
              placeholder="name@example.com"
            />
          </label>
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending}
            className="w-full min-h-[44px] rounded-xl bg-gray-900 text-white text-sm font-bold
              disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
